import { useState, useEffect, useCallback } from 'react'
import { fetchProjectSpend, SpendSummary, SpendWindow } from '../lib/api'

export interface UseProjectSpend {
  summary: SpendSummary | null
  loading: boolean
  error: string | null
  window: SpendWindow
  setWindow: (w: SpendWindow) => void
  /** Re-fetch; pass true to bust the server-side 5-min cache. */
  reload: (refresh: boolean) => Promise<void>
}

/**
 * Fetches authoritative per-project spend from the LiteLLM proxy
 * (`GET /api/projects/:name/spend`). This is the single source of truth for the
 * top-line cost figure (MissionControl) and the detailed breakdown
 * (ProjectSpendCard). Auto-refreshes every 15 min; window changes re-fetch.
 */
export function useProjectSpend(
  projectName: string,
  getAuthHeaders: () => Record<string, string>,
  initialWindow: SpendWindow = '7d',
): UseProjectSpend {
  const [window, setWindow] = useState<SpendWindow>(initialWindow)
  const [summary, setSummary] = useState<SpendSummary | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reload = useCallback(async (refresh: boolean) => {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchProjectSpend(projectName, window, refresh, getAuthHeaders)
      setSummary(data)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'fetch failed')
    } finally {
      setLoading(false)
    }
  }, [projectName, window, getAuthHeaders])

  // Initial + window-change fetch (no cache bust)
  useEffect(() => { reload(false) }, [reload])

  // Auto-refresh every 15 min (no cache bust — uses whatever's in server cache).
  useEffect(() => {
    const id = setInterval(() => reload(false), 15 * 60 * 1000)
    return () => clearInterval(id)
  }, [reload])

  return { summary, loading, error, window, setWindow, reload }
}

/** Authoritative top-line spend = sponsorship credit + cash (LiteLLM-sourced). */
export function totalSpend(summary: SpendSummary | null): number {
  if (!summary) return 0
  return summary.sponsorship_credit.spend + summary.cash.spend
}
